import { useCallback, useEffect, useMemo, useState } from "react";

import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDealActions } from "store";
import { IK1FieldValue, IK1MultiValue, K1_BOOLEAN_CHECKED, K1_BOOLEAN_UNCHECKED } from "store/types";
import { toastify } from "utils/tyostify";

export type K1EditValue = IK1FieldValue;

const valuesEqual = (a: K1EditValue, b: K1EditValue): boolean => {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
};

/**
 * Fetch + edit + save state for the K-1 form.
 *
 * K-1 files are read from a dedicated endpoint (`GET /extraction/k1-tags`), separate from the
 * recommendation-tags flow. Fetching is gated on `isK1` (known up front from `file.is_k1`), so
 * non-K-1 files never hit it. Pending edits are staged locally in the field's own value space
 * (boolean stays boolean, number/text stays string) and converted to the save wire format only
 * on save. The fetched form is the single source of truth — save replaces it with the response.
 */
export const useK1Form = (fileId: number, isK1: boolean) => {
  const { getFileK1TagsAction, saveK1FieldsAction } = useDealActions();
  const { action: fetchForm, data: fetched, setData: setForm, loading, error } = useActionHook(getFileK1TagsAction);
  const [edits, setEdits] = useState<Record<string, K1EditValue>>({});

  useEffect(() => {
    setEdits({});
    if (isK1 && fileId) fetchForm(fileId);
    else setForm(undefined);
  }, [fileId, isK1]);

  const { action: save, loading: saving } = useActionHook(saveK1FieldsAction, {
    onSuccess: (updated) => {
      setForm(updated);
      setEdits({});
      toastify({ message: "K-1 fields saved", type: "success" });
    },
    onReject: () => {
      toastify({ message: "Could not save K-1 fields", type: "error" });
    }
  });

  const form = fetched ?? null;

  const originalByKey = useMemo(() => {
    const map: Record<string, K1EditValue> = {};
    form?.parts.forEach((p) => p.groups.forEach((g) => g.fields.forEach((f) => (map[f.key] = f.value))));
    return map;
  }, [form]);

  const setFieldValue = useCallback(
    (key: string, value: K1EditValue) => {
      setEdits((prev) => {
        const next = { ...prev };
        // Reverting a field to its original value clears the edit (mirrors backend reset semantics).
        if (valuesEqual(originalByKey[key], value)) delete next[key];
        else next[key] = value;
        return next;
      });
    },
    [originalByKey]
  );

  const getFieldValue = useCallback(
    (key: string): K1EditValue => (key in edits ? edits[key] : originalByKey[key]),
    [edits, originalByKey]
  );

  const isFieldEdited = useCallback((key: string) => key in edits, [edits]);

  const isDirty = Object.keys(edits).length > 0;

  const saveEdits = useCallback(async () => {
    if (!isDirty) return;
    const fields: Record<string, string | IK1MultiValue[]> = {};
    Object.entries(edits).forEach(([key, val]) => {
      if (typeof val === "boolean") fields[key] = val ? K1_BOOLEAN_CHECKED : K1_BOOLEAN_UNCHECKED;
      // Multi-value rows: each row's `key` (code) is required, so drop blank-code rows.
      else if (Array.isArray(val)) fields[key] = val.filter((row) => row.key.trim() !== "");
      else fields[key] = val;
    });
    await save(fileId, fields);
  }, [edits, isDirty, save, fileId]);

  return { form, loading, error, isDirty, saving, setFieldValue, getFieldValue, isFieldEdited, saveEdits };
};
